import { lazy, Suspense } from "react";
import { Route, Routes, Navigate, Outlet } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import { NAVIGATION_ROUTE } from "../constants";
import UserLayout from "../layouts/UserLayout/UserLayout";
import AdminLayout from "../layouts/AdminLayout/AdminLayout";
import AdminLoginPage from '../pages/AdminLoginPage';
import { UserManagement, SubscriptionPlans, TransactionHistory } from '../pages/Admin';
import UserProfile from '../pages/Admin/UserProfileAPI';
import AdminAPIDocumentation from '../pages/API/AdminAPIDocumentation';
import AdminProfileAPITabs from '../pages/Admin/AdminProfileAPITabs';
import { CircularProgress, Box, Typography } from '@mui/material';

// Lazy Loading of Pages
const Home = lazy(() => import('./../pages/Homepage'));
const Login = lazy(() => import('./../pages/LoginPage'));
const API = lazy(()=> import('./../pages/API'));
const Profile = lazy(() => import('./../pages/Profile'));
const Subscription = lazy(()=>import('./../pages/Subscription'));
const NotFound = lazy(() => import('./../pages/NotFound'));
const Signup = lazy(() => import('../components/SignupForm'));
const Verify = lazy(() => import('../pages/Verify'));
const ChangePassword = lazy(() => import('../pages/ChangePassword'));
const Billing = lazy(() => import('../pages/Billing'));
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const ResetPassword = lazy(() => import('../pages/ResetPassword'));
const Unauthorized = lazy(() => import('../pages/Unauthorized'));


const AppRoutes = () => {
  return (
    <Suspense fallback={
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={56} thickness={4.5} color="primary" />
        <Typography variant="h6" sx={{ mt: 3, color: 'primary.main', fontWeight: 700, letterSpacing: 1 }}>
          Loading...
        </Typography>
      </Box>
    }>
      <Routes>
        {/* Public Routes */}
        <Route path={NAVIGATION_ROUTE.login} element={<Login />} />
        <Route path={NAVIGATION_ROUTE.home} element={<Home />} />
        <Route path="/client/register" element={<Signup />} />
        <Route path="/verify-email" element={<Verify />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path={NAVIGATION_ROUTE.unauthorized} element={<Unauthorized />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* User Protected Routes */}
        <Route
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <UserLayout />
            </ProtectedRoute>
          }
        >
          <Route path={NAVIGATION_ROUTE.subscription} element={<Subscription />} />
          <Route path={NAVIGATION_ROUTE.api} element={<API />} />
          <Route path={NAVIGATION_ROUTE.profile} element={<Profile />} />
          <Route path={NAVIGATION_ROUTE.changePassword} element={<ChangePassword />} />
          <Route path={NAVIGATION_ROUTE.billing} element={<Billing />} />
          {/* Optionally, catch-all for user routes: */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Admin Protected Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/plans" replace />} />
          {/* <Route path="dashboard" element={<Dashboard />} /> */}
          {/* Removed: <Route path="subscription" element={<Subscription />} /> */}
          <Route path="users" element={<UserManagement />} />
          <Route path="plans" element={<SubscriptionPlans />} />
          <Route path="transactions" element={<TransactionHistory />} />
          <Route path="profile" element={<UserProfile />} />
          <Route path="api" element={<AdminAPIDocumentation />} />
          {/* Optionally, catch-all for admin routes: */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Fallback NotFound for all unmatched routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
